import DOMPurify from "dompurify";

import type { Icd10DescriptionMap } from "@/lib/billing/icd10-descriptions";
import { formatDate, formatTime } from "@/lib/format-data";
import {
  type Icd10CodeLine,
  icd10CodeLines,
  isIcd10CodesQuestion,
} from "@/lib/icd10-codes-field";
import { computeHiddenIds, noteAnswerValue } from "@/lib/question-conditions";

import type { Annotation } from "@/types/annotation";
import {
  Disclaimer,
  PlaceholderImage,
  QuestionType,
  ScribeAttachments,
  Statement,
} from "@/types/forms/question";
import type {
  NoteAnswerDetail,
  NoteQuestionDetail,
  NoteTemplateTableItem,
} from "@/types/notes";
import { LEGACY_TEMPLATE_DESCRIPTION_PREFIX, NoteDetail } from "@/types/notes";

/**
 * Marks a link from the version history menu. A provider who picked an older
 * version on purpose stays on it; every other way of reaching an older version
 * (Back, a task, a list that was loaded earlier) moves on to the newest one.
 */
export const NOTE_HISTORY_PARAM = "history";

/**
 * True only for values produced by the rich-text answer serializer, which
 * always opens with a block element (`<p>`, `<h1>`–`<h3>`, `<ul>`, `<ol>`, or
 * `<blockquote>`). Matching on the *leading* block tag — not just a tag
 * appearing anywhere — keeps legacy plain-text answers that merely contain
 * tag-like text ("Patient wrote <strong>not taking meds</strong>", "I feel
 * <great>") on the verbatim render path instead of feeding them through an
 * HTML parser that would drop/reformat the literal text.
 */
export function looksLikeRichHtml(value: string): boolean {
  return /^<(p|h[1-6]|ul|ol|blockquote)(\s|>)/i.test(value);
}

const isListElement = (node: Node | null): boolean =>
  node?.nodeType === Node.ELEMENT_NODE &&
  ((node as Element).tagName === "UL" || (node as Element).tagName === "OL");

/** A node's own `<ul>`/`<ol>` children. Read off `children` rather than a
 *  `:scope >` selector, which jsdom resolves as any descendant — so a test
 *  would see a flattened list where the browser sees a nested one. */
const childLists = (node: Element): Element[] =>
  Array.from(node.children).filter(isListElement);

/** A node's own sub-lists that will contribute at least one bullet. An empty one
 *  emits nothing, so it neither needs a break before it nor supplies the
 *  trailing break its parent item would otherwise have to append. */
const contentfulChildLists = (node: Element): Element[] =>
  childLists(node).filter((list) => list.querySelector("li"));

/** Block elements this module appends a newline to below. A sub-list following
 *  one already has its break and must not be given a second. */
const TRAILING_BREAK_BLOCKS = "p, h1, h2, h3, h4, h5, h6, blockquote";

const suppliesTrailingBreak = (node: Node | null): boolean =>
  node?.nodeType === Node.ELEMENT_NODE &&
  (node as Element).matches(TRAILING_BREAK_BLOCKS);

/** Trim the whitespace an HTML source may have left touching a nested list, so
 *  the single break inserted before it is all that separates it from its parent
 *  item's text. Left in place it survives as a second break, which the newline
 *  collapse then turns into a blank line between parent and child. A node
 *  trimmed away entirely is dropped, so a sub-list that ends its item still
 *  reads as the item's last child. */
const trimTextTouching = (sublist: Element): void => {
  const trim = (node: Node | null, pattern: RegExp) => {
    if (node?.nodeType !== Node.TEXT_NODE) return;
    const trimmed = (node.textContent ?? "").replace(pattern, "");
    if (trimmed) node.textContent = trimmed;
    else node.parentNode?.removeChild(node);
  };
  trim(sublist.previousSibling, /\s+$/);
  trim(sublist.nextSibling, /^\s+/);
};

/**
 * Strips HTML tags and converts to clean plain text.
 * Uses DOMParser for robust HTML parsing with list handling.
 */
export function stripHtmlToText(html: string): string {
  if (!html || typeof html !== "string") return "";

  // Quick check: if no HTML tags, return as-is
  if (!html.includes("<")) return html;

  // Use DOMParser to parse HTML and extract text content
  const doc = new DOMParser().parseFromString(html, "text/html");

  // Convert list items to bullet points before extracting text
  // Using prepend() preserves nested list structures (vs textContent which is destructive)
  doc.querySelectorAll("li").forEach((li) => {
    // A nested list is a block of its own items, so start it on a new line
    // instead of running its first bullet on from this item's text. Done before
    // the bullet is prepended, so a sub-list that opens the item can never see
    // the marker as the text node to trim.
    const sublists = contentfulChildLists(li);
    sublists.forEach((sublist) => {
      trimTextTouching(sublist);
      // Skip the break when whatever precedes the sub-list already ends in one:
      // another list (its last bullet supplied it), or a block element like the
      // `<p>` the rich-text editor wraps every list item's text in.
      const previous = sublist.previousSibling;
      // Only a sub-list that actually emits bullets supplies the break; an empty
      // one sits in `previous` without contributing anything.
      if (
        !sublists.includes(previous as Element) &&
        !suppliesTrailingBreak(previous)
      ) {
        sublist.before("\n");
      }
    });
    li.prepend("• ");
    // Only append the item's own break when nothing already ends in one: a
    // sub-list supplies it through its last bullet, and so does the `<p>` the
    // rich-text editor wraps every item's text in. An empty sub-list supplies
    // nothing, so look past it for what really ends the item.
    let tail = li.lastChild;
    while (isListElement(tail) && !sublists.includes(tail as Element)) {
      tail = tail!.previousSibling;
    }
    if (!sublists.includes(tail as Element) && !suppliesTrailingBreak(tail)) {
      li.append("\n");
    }
  });

  // textContent ignores element boundaries, so preserve line/paragraph breaks
  // from block HTML (rich note answers stored as <p>/<h1-6>/<blockquote>/<br>)
  // before extracting text — otherwise adjacent blocks concatenate into one
  // run-on.
  doc.querySelectorAll("br").forEach((br) => br.replaceWith("\n"));
  doc.querySelectorAll(TRAILING_BREAK_BLOCKS).forEach((el) => el.append("\n"));

  // Get the text content
  let text = doc.body.textContent || "";

  // Clean up whitespace
  text = text.replace(/\n\s*\n+/g, "\n\n"); // Collapse multiple newlines
  text = text.trim();

  return text;
}

/** Escape text destined for the printed HTML document. */
export function escapeNoteHtml(value: string): string {
  return value.replace(
    /[&<>'"]/g,
    (character) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        "'": "&#39;",
        '"': "&quot;",
      })[character] ?? character,
  );
}

/**
 * The tags a rich-text answer may contribute to the printed document. Mirrors
 * the editor's own extension set (bold, italic, underline, headings, lists,
 * quotes, coloured spans) — anything else a stored answer happens to contain is
 * dropped rather than trusted, since answer HTML can predate that editor or
 * arrive from an import.
 */
const RICH_ANSWER_ALLOWED_TAGS = [
  "p",
  "br",
  "strong",
  "b",
  "em",
  "i",
  "u",
  "s",
  "h1",
  "h2",
  "h3",
  "ul",
  "ol",
  "li",
  "blockquote",
  "span",
];

/**
 * Sanitize a rich-text answer for the printed document. Underline and colour
 * survive here where the Markdown conversion had to discard them, and DOMPurify
 * — not Markdown escaping — is what keeps stored markup from executing.
 */
/**
 * The CSS properties the note editor actually writes — text colour, highlight,
 * and the emphasis marks that can arrive as inline style from a paste.
 * DOMPurify filters *attributes*, not the declarations inside `style`, so
 * without this an imported answer could carry `position:fixed;inset:0` and
 * paper over the rest of the clinical document, or pull an external image in
 * via `background-image:url(...)` the moment the print window is written.
 */
const RICH_ANSWER_ALLOWED_STYLE_PROPS = new Set([
  "color",
  "background-color",
  "font-size",
  "font-style",
  "font-weight",
  "text-decoration",
  "text-decoration-line",
]);

/** Strip every declaration outside the allowlist, and any value fetching a URL. */
function restrictInlineStyles(root: ParentNode): void {
  for (const element of Array.from(root.querySelectorAll("[style]"))) {
    const declarations = (element.getAttribute("style") ?? "")
      .split(";")
      .map((declaration) => declaration.trim())
      .filter(Boolean)
      .filter((declaration) => {
        const separator = declaration.indexOf(":");
        if (separator === -1) return false;
        const property = declaration.slice(0, separator).trim().toLowerCase();
        const value = declaration.slice(separator + 1).toLowerCase();
        if (!RICH_ANSWER_ALLOWED_STYLE_PROPS.has(property)) return false;
        return !/url\(|expression\(|[@\\]/.test(value);
      });

    if (declarations.length > 0) {
      element.setAttribute("style", `${declarations.join("; ")};`);
    } else {
      element.removeAttribute("style");
    }
  }
}

export function richHtmlToSafeHtml(html: string): string {
  const clean = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: RICH_ANSWER_ALLOWED_TAGS,
    // `style` carries the editor's text and highlight colours; the declarations
    // inside it are narrowed to those by `restrictInlineStyles` below.
    ALLOWED_ATTR: ["style"],
  });

  const doc = new DOMParser().parseFromString(clean, "text/html");
  restrictInlineStyles(doc.body);
  reNestStraySublists(doc.body);
  return doc.body.innerHTML;
}

/**
 * Move a sub-list the model wrote *beside* its parent item back inside it.
 *
 * The scribe asks for light HTML and gets nested bullets back with the sub-list
 * as a sibling of the `<li>` it belongs to rather than a child of it — see
 * #9821. `<ul>` directly inside `<ul>` is not a valid list content model, so
 * every consumer guesses, and the printed document renders each stray list as
 * its own block instead of as an indentation.
 *
 * `normalize_rich_html` repairs this on the way in, so newly generated answers
 * arrive well-formed. This is for the ones already stored: the backfill over
 * them has not been run, and print has to render what is there today.
 *
 * Conservative on purpose, like the backend does it: a stray list only moves
 * when the node before it really is the item it belongs to. Anything else is
 * left exactly as found.
 */
function reNestStraySublists(root: ParentNode): void {
  // Read children directly rather than through a `:scope >` selector: jsdom
  // resolves that as any descendant, so a test would see a flattened list where
  // the browser sees a nested one, and this repair would look untested.
  Array.from(root.querySelectorAll("ul, ol")).forEach((list) => {
    Array.from(list.children).forEach((child) => {
      if (!isListElement(child)) return;
      const previous = child.previousElementSibling;
      if (previous?.tagName === "LI") previous.appendChild(child);
    });
  });
}

/**
 * A legacy plain-text answer as HTML for the printed document.
 *
 * Escaped first, so the only markup in the result is what this function adds,
 * and it adds only two things the scribe actually writes into plain-text
 * answers: `**text**` emphasis ("**Chief Complaint:** Follow-up for
 * Semaglutide") and dash bullets ("- Glabella: 20 units"). Printing either
 * as raw punctuation is what a clinical document must not do.
 *
 * A dash only starts a bullet at the beginning of a line, so a hyphenated word
 * or a "5 - 10 mg" range is untouched. `#` is never a heading: in real notes a
 * leading `#` is a gauge or lot number — "# 22 Nexiva inserted RAC",
 * "# LG77403" — which the old Markdown path rendered as page-width headings.
 *
 * `blocks` is false where the answer is rendered inside a paragraph (a discrete
 * answer on one line), since a list cannot legally nest there.
 */
function plainTextToPrintHtml(text: string, blocks = true): string {
  const inline = (value: string) =>
    value.replace(/\*\*([^*\n]+)\*\*/g, "<strong>$1</strong>");

  const escaped = escapeNoteHtml(text);
  if (!blocks) return inline(escaped).replace(/\n/g, "<br/>");

  const out: string[] = [];
  let bullets: string[] = [];
  let paragraph: string[] = [];

  const flushBullets = () => {
    if (!bullets.length) return;
    out.push(`<ul>${bullets.map((b) => `<li>${b}</li>`).join("")}</ul>`);
    bullets = [];
  };
  const flushParagraph = () => {
    if (!paragraph.length) return;
    out.push(`<p>${paragraph.join("<br/>")}</p>`);
    paragraph = [];
  };

  for (const line of escaped.split("\n")) {
    const bullet = line.match(/^\s*-\s+(.+)$/);
    if (bullet) {
      flushParagraph();
      bullets.push(inline(bullet[1]));
      continue;
    }
    flushBullets();
    if (line.trim()) paragraph.push(inline(line));
    else flushParagraph();
  }
  flushBullets();
  flushParagraph();

  return out.join("");
}

/**
 * A captured signature as an `<img>` for the printed document. Only the two
 * shapes signatures actually take are allowed through — an inline data image or
 * an https URL — so a stored `javascript:` value can never reach the src.
 */
function signatureImageHtml(url: string, alt: string): string {
  const trimmed = url.trim();
  const isSafe =
    trimmed.startsWith("data:image/") || trimmed.startsWith("https://");
  if (!isSafe) return "";

  return `<img class="note-signature-image" src="${escapeNoteHtml(trimmed)}" alt="${escapeNoteHtml(alt)}" />`;
}

export interface ConvertAnswerChangesResult {
  answers: Array<{
    questionId: string;
    data: Record<string, any>;
    noteAttachmentId?: string;
  }>;
  /** Answer IDs that could not be mapped to questions (data may be lost) */
  skippedAnswerIds: string[];
}

/**
 * Converts answer changes (keyed by answerId or temp-{questionId}) to the format expected by NoteCreate.
 * Handles both temporary answer IDs (temp-{questionId}) and existing answer IDs by looking them up.
 *
 * @param answerChanges - Record of answer changes, keyed by answerId (e.g., "na_123") or temp ID (e.g., "temp-nq_456")
 * @param existingAnswers - Optional array of existing answers to look up questionIds from answerIds
 * @returns Object containing answers array and any skipped answer IDs for error handling
 */
export function convertAnswerChangesToAnswersWithErrors(
  answerChanges: Record<string, Record<string, any>>,
  existingAnswers?: NoteAnswerDetail[],
): ConvertAnswerChangesResult {
  const answersToCreate: ConvertAnswerChangesResult["answers"] = [];
  const skippedAnswerIds: string[] = [];

  if (Object.keys(answerChanges).length === 0) {
    return { answers: answersToCreate, skippedAnswerIds };
  }

  // Create a map of existing answers by answerId -> questionId for reverse lookup
  const answerIdToQuestionId = new Map<string, string>();
  if (existingAnswers) {
    existingAnswers.forEach((answer) => {
      answerIdToQuestionId.set(answer.id, answer.questionId);
    });
  }

  Object.entries(answerChanges).forEach(([answerId, data]) => {
    // Extract noteAttachmentId if it exists in the data
    const { noteAttachmentId, ...answerData } = data;

    // Determine questionId: if it's a temp ID (temp-{questionId}), extract it
    // Otherwise, look it up from existing answers
    let questionId: string;
    if (answerId.startsWith("temp-")) {
      questionId = answerId.replace("temp-", "");
    } else {
      // Find questionId from existing answer
      const foundQuestionId = answerIdToQuestionId.get(answerId);
      if (!foundQuestionId) {
        skippedAnswerIds.push(answerId);
        return; // Skip this answer but track it
      }
      questionId = foundQuestionId;
    }

    answersToCreate.push({
      questionId,
      data: answerData,
      noteAttachmentId,
    });
  });

  return { answers: answersToCreate, skippedAnswerIds };
}

/**
 * Converts answer changes to answers array (legacy function for backward compatibility).
 * Use convertAnswerChangesToAnswersWithErrors for better error handling.
 */
export function convertAnswerChangesToAnswers(
  answerChanges: Record<string, Record<string, any>>,
  existingAnswers?: NoteAnswerDetail[],
): Array<{
  questionId: string;
  data: Record<string, any>;
  noteAttachmentId?: string;
}> {
  const result = convertAnswerChangesToAnswersWithErrors(
    answerChanges,
    existingAnswers,
  );
  if (result.skippedAnswerIds.length > 0) {
    console.warn(
      `Could not find questionIds for answers: ${result.skippedAnswerIds.join(", ")}`,
    );
  }
  return result.answers;
}

/**
 * Finds a SCRIBE_ATTACHMENTS question in a template.
 * If placeholderName is provided, finds the specific question containing that placeholder.
 * Otherwise, returns the first SCRIBE_ATTACHMENTS question found.
 */
export function findScribeAttachmentsQuestion(
  template: NoteTemplateTableItem | null | undefined,
  placeholderName?: string,
) {
  if (!template?.questions) return undefined;

  if (placeholderName) {
    return template.questions.find((q) => {
      if (q.questionType !== QuestionType.SCRIBE_ATTACHMENTS) return false;
      const data = q.data as ScribeAttachments;
      return data?.placeholders?.some((p) => p.name === placeholderName);
    });
  }

  return template.questions.find(
    (q) => q.questionType === QuestionType.SCRIBE_ATTACHMENTS,
  );
}

/**
 * Finds the answer ID for a SCRIBE_ATTACHMENTS question in a note.
 * Returns the answer ID if found, or undefined if not found.
 *
 * @param note - The note detail containing answers
 * @param template - The template containing questions
 * @param placeholderName - Optional placeholder name to match against a specific question
 * @returns The answer ID for the SCRIBE_ATTACHMENTS question, or undefined if not found
 */
export function findScribeAttachmentsAnswerId(
  note: NoteDetail | null | undefined,
  template: NoteTemplateTableItem | null | undefined,
  placeholderName?: string,
): string | undefined {
  if (!note || !template?.questions || !note.answers) {
    return undefined;
  }

  // Find SCRIBE_ATTACHMENTS questions
  for (const question of template.questions) {
    if (question.questionType === QuestionType.SCRIBE_ATTACHMENTS) {
      // If placeholderName is provided, check if this question contains it
      if (placeholderName) {
        const data = question.data as ScribeAttachments;
        const hasPlaceholder = data?.placeholders?.some(
          (p) => p.name === placeholderName,
        );
        if (!hasPlaceholder) {
          continue; // Skip this question if it doesn't have the placeholder
        }
      }

      // Find the answer for this question
      const answer = note.answers.find((a) => a.questionId === question.id);
      if (answer) {
        return answer.id;
      }
    }
  }

  return undefined;
}

/**
 * Checks if a template has any SCRIBE_ATTACHMENTS questions.
 * Returns true if the template has at least one non-archived SCRIBE_ATTACHMENTS question.
 *
 * @param template - The template to check, can be null or undefined
 * @returns true if template has SCRIBE_ATTACHMENTS questions, false otherwise
 */
export function hasScribeAttachmentsQuestions(
  template: NoteTemplateTableItem | null | undefined,
): boolean {
  if (!template?.questions) {
    return false;
  }

  return template.questions.some(
    (question) =>
      !question.isArchived &&
      question.questionType === QuestionType.SCRIBE_ATTACHMENTS,
  );
}

/**
 * Gets the placeholders that are copied onto every new note: those on
 * SCRIBE_ATTACHMENTS questions that don't use the pick-from-gallery option.
 */
export function getAutoAddedTemplatePlaceholders(
  template: NoteTemplateTableItem | null | undefined,
): PlaceholderImage[] {
  return getScribeAttachmentsData(template)
    .filter((data) => !data.pickFromGallery)
    .flatMap((data) => data.placeholders ?? []);
}

/**
 * Gets the template images a provider can pick from the gallery: image
 * placeholders on SCRIBE_ATTACHMENTS questions with pick-from-gallery on.
 */
export function getTemplateGalleryImages(
  template: NoteTemplateTableItem | null | undefined,
): PlaceholderImage[] {
  return getScribeAttachmentsData(template)
    .filter((data) => data.pickFromGallery)
    .flatMap((data) => data.placeholders ?? [])
    .filter((placeholder) => !!placeholder.gcsPath);
}

/**
 * The template's marks as new marks for a patient's copy of the image: fresh
 * ids and today's date, no audit stamps, and no lot, since the lot is whatever
 * this visit draws from.
 */
export function copyTemplateAnnotations(
  annotations: Annotation[] | undefined,
): Annotation[] {
  if (!annotations?.length) return [];
  const now = new Date();
  const createdAt = now.toISOString();
  return annotations.map((annotation, index) => {
    const {
      createdBy: _createdBy,
      updatedAt: _updatedAt,
      updatedBy: _updatedBy,
      ...rest
    } = annotation;
    const copy = {
      ...rest,
      id: `annotation_${now.getTime()}_${index}_${Math.random().toString(36).slice(2, 11)}`,
      createdAt,
    } as Annotation;
    if ("lotNumber" in copy) copy.lotNumber = null;
    if ("shipmentId" in copy) copy.shipmentId = null;
    return copy;
  });
}

function getScribeAttachmentsData(
  template: NoteTemplateTableItem | null | undefined,
): ScribeAttachments[] {
  return (template?.questions ?? [])
    .filter(
      (question) =>
        !question.isArchived &&
        question.questionType === QuestionType.SCRIBE_ATTACHMENTS,
    )
    .map((question) => question.data as ScribeAttachments)
    .filter(Boolean);
}

/**
 * Formats note answers from NoteDetail into readable text content.
 * Returns formatted answers if available, otherwise returns the summary.
 */
interface FormatNoteAnswersToTextOptions {
  /** Emit section names as plain text instead of markdown headings. */
  plainSectionHeadings?: boolean;
  /** Avoid repeating a section name as the first answer label in that section. */
  dedupeSectionLabels?: boolean;
  /** Avoid markdown syntax in generated note text. Used for clipboard copies. */
  plainText?: boolean;
  /**
   * Render each question as its own subheading with the answer as prose
   * beneath, instead of "**Label:** value" on a single line. Clinical
   * documentation is read as continuous prose under scannable subheadings, so
   * a printed note must not collapse into a list of label/value fragments.
   * Questions whose answer is blank are dropped rather than printed as a bare
   * label.
   */
  proseLayout?: boolean;
  /**
   * Emit the note as sanitized HTML instead of Markdown. The printed document
   * takes this path: Markdown cannot express underline or colour, so routing
   * rich answers through it silently dropped formatting the editor offers.
   */
  outputHtml?: boolean;
  /** Names each code on an ICD-10 Codes answer; a code missing here prints bare. */
  icd10Descriptions?: Icd10DescriptionMap;
}

// An ICD-10 Codes answer reads as a diagnosis list, one code per line, its
// description beside it and the provider's comment beneath — not the bare
// comma-joined codes a generic multi-value answer prints as.
const icd10LinesToText = (lines: Icd10CodeLine[], plainText: boolean) =>
  lines
    .map(({ code, description, comment }) => {
      const head = `${plainText ? "•" : "-"} ${[code, description].filter(Boolean).join(" ")}`;
      const body = comment
        ? comment
            .split("\n")
            .map((line) => `\n  ${line}`)
            .join("")
        : "";
      return head + body;
    })
    .join("\n");

const icd10LinesToHtml = (lines: Icd10CodeLine[]) =>
  `<ul>${lines
    .map(
      ({ code, description, comment }) =>
        `<li><strong>${escapeNoteHtml(code)}</strong>${
          description ? ` ${escapeNoteHtml(description)}` : ""
        }${
          comment
            ? `<div style="padding-left: 1.25rem">${escapeNoteHtml(
                comment,
              ).replace(/\n/g, "<br/>")}</div>`
            : ""
        }</li>`,
    )
    .join("")}</ul>`;

const normalizeNoteLabel = (label: string | null | undefined) =>
  label ? label.trim().replace(/:$/, "").toLocaleLowerCase() : "";

const stripDuplicateLeadingLabel = (text: string, label: string) => {
  const trimmedLabel = label.trim();
  if (!trimmedLabel) return text;

  if (text.startsWith(`${trimmedLabel}: `)) {
    return text.slice(trimmedLabel.length + 2);
  }

  if (text.startsWith(`${trimmedLabel}:\n`)) {
    return text.slice(trimmedLabel.length + 2).trimStart();
  }

  if (text.startsWith(`${trimmedLabel}\n`)) {
    return text.slice(trimmedLabel.length + 1).trimStart();
  }

  return text;
};

// Wrap a line's leading question label in markdown bold so exported/printed
// notes emphasize the question (e.g. "Chief Complaint", "HPI") over its answer.
const boldLeadingLabel = (text: string, label: string) => {
  // Drop a trailing colon so a label like "Chief Complaint:" doesn't wrap the
  // colon inside the bold (which would render "**Chief Complaint:**: value").
  const trimmedLabel = label.trim().replace(/:$/, "");
  if (!trimmedLabel) return text;

  if (
    text.startsWith(`${trimmedLabel}:`) ||
    text.startsWith(`${trimmedLabel}\n`)
  ) {
    return `**${trimmedLabel}**${text.slice(trimmedLabel.length)}`;
  }

  return text;
};

/** Record of answer changes keyed by answerId (e.g. "na_123") or by a temp id
 *  ("temp-{questionId}") for a question with no saved answer yet. Matches the
 *  shape of `NoteAnswersSection`'s `localAnswerChanges` prop. */
export type NoteAnswerChanges = Record<string, Record<string, any>>;

/**
 * Effective answers for a note: saved answers with any unsaved draft edits
 * applied on top, plus draft-only answers for questions that have no saved
 * answer yet. Mirrors the merge `NoteAnswersSection` performs to build the
 * answer map it renders from, so anything that has to agree with what the
 * editor currently shows (print, copy, conditional-visibility) can reuse the
 * exact same logic instead of drifting out of sync with it.
 *
 * With no `localAnswerChanges`, returns `noteDetail.answers` unchanged
 * (including archived answers) to preserve existing callers' behavior exactly.
 */
export function mergeAnswersWithLocalChanges(
  noteDetail: NoteDetail,
  localAnswerChanges?: NoteAnswerChanges,
): NoteAnswerDetail[] {
  const baseAnswers = noteDetail.answers ?? [];
  if (!localAnswerChanges || Object.keys(localAnswerChanges).length === 0) {
    return baseAnswers;
  }

  const merged: NoteAnswerDetail[] = [];
  const seenQuestionIds = new Set<string>();
  baseAnswers.forEach((a) => {
    if ((a as { isArchived?: boolean }).isArchived) return;
    const localChanges = localAnswerChanges[a.id];
    merged.push(localChanges ? { ...a, data: localChanges } : a);
    seenQuestionIds.add(a.questionId);
  });

  // Draft-only answers (no saved answer yet), keyed by temp-{questionId}.
  const questionIds = new Set(
    (noteDetail.template?.questions ?? []).map((q) => q.id),
  );
  Object.entries(localAnswerChanges).forEach(([answerId, data]) => {
    if (!answerId.startsWith("temp-")) return;
    const questionId = answerId.replace("temp-", "");
    if (seenQuestionIds.has(questionId) || !questionIds.has(questionId)) {
      return;
    }
    merged.push({
      id: answerId,
      noteId: noteDetail.id,
      questionId,
      data,
      createdDate: new Date().toISOString(),
    });
  });

  return merged;
}

/**
 * A note's own date, as the `YYYY-MM-DD` calendar day `computeHiddenIds`
 * expects for its `evaluationDate`, read in UTC.
 *
 * A YEARS_AGO_MINIMUM/MAXIMUM rule measures the trigger answer's age against
 * `evaluationDate`, which defaults to "today" when omitted. That default is
 * correct while a note is being drafted, but once a note is signed, its
 * conditional content must stop moving: a rule that was satisfied (or wasn't)
 * at sign-off cannot silently flip months later just because the calendar
 * moved on -- the note is governed by the facts as of when it was written, the
 * same immutability promise the backend now enforces by pinning
 * `evaluation_date` to `note.created_date.date()`
 * (`decoda/apps/api/routes/patient_portal/notes.py`) instead of "today". This
 * mirrors that on the frontend so the editor, print/copy, and the patient
 * portal all agree on which side of an age-gate boundary a signed note falls.
 *
 * Reads the UTC calendar date deliberately, not the browser's local one:
 * `created_date` is stored and read back as a UTC-aware `datetime` on the
 * server (`UTCDateTime` in `decoda/store/shared/custom_columns/utc_datetime.py`),
 * and `.date()` there takes the calendar day of that UTC instant with no
 * timezone conversion. `getUTCFullYear`/`getUTCMonth`/`getUTCDate` read the
 * same instant the same way; the local-time equivalents would read a
 * different (and for any timezone west of UTC, potentially wrong-side-of-
 * midnight) calendar day. See the UTC-vs-local trap called out in
 * `parseCalendarDate` in `question-conditions.ts` -- this is the same failure
 * mode, avoided the same way.
 *
 * Returns undefined when `createdDate` is missing or fails to parse, so a
 * malformed date can never throw and simply leaves the caller's "today"
 * default in place rather than silently hiding content.
 */
export function noteEvaluationDate(
  noteDetail: Pick<NoteDetail, "createdDate">,
): string | undefined {
  // Typed as `Date` on NoteDetail, but the API client hands back whatever
  // `JSON.parse` produced (see `console/src/api/api-config.ts`) with no date
  // reviver, so in practice this is an ISO string at runtime. Accept either.
  const raw: unknown = noteDetail.createdDate;
  const date =
    raw instanceof Date ? raw : typeof raw === "string" ? new Date(raw) : null;
  if (!date || isNaN(date.getTime())) return undefined;

  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Ids of the questions a note hides, counting every question inside a hidden
 * section.
 *
 * `localAnswerChanges`, when passed, is merged in so visibility reacts to
 * unsaved draft edits the same way the note editor does (NoteAnswersSection) —
 * otherwise a gated question can show on screen but be missing from a
 * print/copy taken before saving, or vice versa.
 */
export function hiddenNoteQuestionIds(
  noteDetail: NoteDetail,
  localAnswerChanges?: NoteAnswerChanges,
): Set<string> {
  const template = noteDetail.template;
  if (!template) return new Set();

  const answerByQuestionId = new Map(
    mergeAnswersWithLocalChanges(noteDetail, localAnswerChanges).map((a) => [
      a.questionId,
      a,
    ]),
  );
  const questionById = new Map(template.questions.map((q) => [q.id, q]));
  const evalCtx = {
    getValue: (dependentQuestionId: string) =>
      noteAnswerValue(
        answerByQuestionId.get(dependentQuestionId),
        questionById.get(dependentQuestionId)?.questionType,
      ),
    getQuestionType: (dependentQuestionId: string) =>
      questionById.get(dependentQuestionId)?.questionType,
    // See NoteAnswersSection: never hide charted content over an unresolvable trigger.
    failOpenOnUnknownDependency: true,
    // Pin age-gate rules (YEARS_AGO_MINIMUM/MAXIMUM) to the note's own date, not
    // "today" -- see noteEvaluationDate. Undefined (an unparseable/missing
    // createdDate) falls back to computeHiddenIds's own "today" default.
    evaluationDate: noteEvaluationDate(noteDetail),
  };

  // Must use the same settling pass as the editor, or a chained rule renders one
  // way on screen and another way in the printout and clipboard copy.
  return computeHiddenIds(template.questions, template.sections ?? [], evalCtx)
    .questionIds;
}

/**
 * Template questions in display order, with conditionally-hidden questions (and
 * every question inside a hidden section) removed.
 *
 * Hiding a question never clears its stored answer, and the AI scribe answers
 * gated questions regardless, so anything that renders a note has to evaluate
 * conditions rather than assume a hidden question has no value.
 */
function visibleSortedQuestions(
  noteDetail: NoteDetail,
  localAnswerChanges?: NoteAnswerChanges,
): NoteQuestionDetail[] {
  const hidden = hiddenNoteQuestionIds(noteDetail, localAnswerChanges);
  return [...(noteDetail.template?.questions ?? [])]
    .filter((q) => !hidden.has(q.id))
    .sort((a, b) => a.position - b.position);
}

/**
 * Whether a question contributes any text to the export. Display-only types
 * print from their own config; everything else needs an answer that renders —
 * see {@link answerProducesText}, which mirrors the emit/skip branches in
 * {@link formatNoteAnswersToText}.
 */
function questionProducesText(
  question: NoteQuestionDetail,
  answer: NoteAnswerDetail | undefined,
): boolean {
  if (question.questionType === QuestionType.DISCLAIMER) {
    return Boolean((question.data as Disclaimer | undefined)?.content?.trim());
  }
  if (question.questionType === QuestionType.STATEMENT) {
    return ((question.data as Statement | undefined)?.points?.length ?? 0) > 0;
  }
  return answer ? answerProducesText(question, answer) : false;
}

/**
 * Whether a question that would have printed was held back by a filter — either
 * a condition that hid it or a print-time exclusion the provider chose.
 *
 * This separates "this note's content was filtered down to nothing" from "this
 * note never had structured content", which matters because callers fall back
 * to the note's raw `summary` for the latter and the summary is neither
 * condition-filtered nor exclusion-filtered. Without the distinction, gating
 * every question — or unchecking every part in the print dialog — would put the
 * withheld content straight back into the printed copy.
 */
function hasWithheldPrintableContent(
  noteDetail: NoteDetail,
  excludedQuestionIds?: ReadonlySet<string>,
  localAnswerChanges?: NoteAnswerChanges,
): boolean {
  const template = noteDetail.template;
  if (!template) return false;

  const visibleQuestionIds = new Set(
    visibleSortedQuestions(noteDetail, localAnswerChanges).map((q) => q.id),
  );
  const answerByQuestionId = new Map(
    mergeAnswersWithLocalChanges(noteDetail, localAnswerChanges).map((a) => [
      a.questionId,
      a,
    ]),
  );

  return template.questions.some((question) => {
    const isWithheld =
      !visibleQuestionIds.has(question.id) ||
      Boolean(excludedQuestionIds?.has(question.id));
    return (
      isWithheld &&
      questionProducesText(question, answerByQuestionId.get(question.id))
    );
  });
}

// Promote the question label to a subheading and leave the answer as prose
// beneath it. Returns "" when the answer is blank so the caller can drop the
// question instead of emitting a heading with nothing under it.
const proseLabelledBlock = (
  text: string,
  label: string,
  plainText: boolean,
) => {
  const body = stripDuplicateLeadingLabel(text, label).trim();
  if (!body) return "";

  const trimmedLabel = label.trim().replace(/:$/, "");
  if (!trimmedLabel) return body;

  return `${plainText ? trimmedLabel : `### ${trimmedLabel}`}\n\n${body}`;
};

// One question as HTML: narrative answers get a subheading with the answer
// beneath, discrete answers stay on one line. Returns "" for a blank answer so
// the caller drops the question rather than printing a bare label.
/**
 * Whether a fragment of answer HTML would put anything on the page.
 *
 * The editor persists the structure it is given, so an answer a provider left
 * alone can still be stored as `<h2></h2>` or `<ul><li><p></p></li></ul>`.
 * Sanitization keeps those tags, so a non-empty string is not evidence of a
 * non-empty answer — only visible text, or an embedded image, is.
 */
const htmlHasVisibleContent = (html: string): boolean => {
  if (!html.trim()) return false;
  const doc = new DOMParser().parseFromString(html, "text/html");
  return (
    doc.body.textContent?.trim() !== "" ||
    doc.body.querySelector("img") !== null
  );
};

const htmlLabelledBlock = (
  valueHtml: string,
  label: string,
  prose: boolean,
) => {
  const body = valueHtml.trim();
  if (!htmlHasVisibleContent(body)) return "";

  const trimmedLabel = label.trim().replace(/:$/, "");
  if (!trimmedLabel) return body;

  return prose
    ? `<h3>${escapeNoteHtml(trimmedLabel)}</h3>${body}`
    : `<p><strong>${escapeNoteHtml(trimmedLabel)}</strong>: ${body}</p>`;
};

export function formatNoteAnswersToText(
  noteDetail: NoteDetail,
  // Question IDs to omit from the output. Used by the print flow to let a
  // provider exclude selected parts of a note from the printed/patient copy.
  excludedQuestionIds?: ReadonlySet<string>,
  options: FormatNoteAnswersToTextOptions = {},
  // Unsaved draft edits to merge in on top of the saved answers. Optional and
  // defaults to today's saved-only behavior — pass this only from a call site
  // that actually has draft state (e.g. the note editor's local answer
  // changes), so a print/copy taken mid-edit matches what's on screen instead
  // of silently falling back to the last-saved answers.
  localAnswerChanges?: NoteAnswerChanges,
): string | undefined {
  const mergedAnswers = mergeAnswersWithLocalChanges(
    noteDetail,
    localAnswerChanges,
  );
  // Display-only types (disclaimer/statement) print from their config even
  // when the note has no answers, so don't gate solely on answers existing.
  const hasDisplayOnlyContent = (noteDetail.template?.questions ?? []).some(
    (q) =>
      q.questionType === QuestionType.DISCLAIMER ||
      q.questionType === QuestionType.STATEMENT,
  );
  if (
    noteDetail.template &&
    (mergedAnswers.length > 0 || hasDisplayOnlyContent)
  ) {
    // Iterate questions in template position order so the printed output
    // matches what the editor shows. The backend returns answers in arbitrary
    // (insertion) order, so iterating answers directly would print fields out
    // of order.
    const answerByQuestionId = new Map(
      mergedAnswers.map((a) => [a.questionId, a]),
    );
    const sortedQuestions = visibleSortedQuestions(
      noteDetail,
      localAnswerChanges,
    );

    // Build section name lookup
    const sectionNameMap = new Map<string, string>();
    if (noteDetail.template.sections) {
      for (const section of noteDetail.template.sections) {
        sectionNameMap.set(section.id, section.name);
      }
    }

    // Build answer text map: questionId → formatted "Label: value" string
    // `prose` marks narrative answers — the ones that read as sentences and so
    // earn their own subheading under `proseLayout`. Discrete answers (a number,
    // a date, a picked option) stay inline as "Label: value"; a subheading over
    // a single vital sign reads worse than the fragment it replaced.
    const answerTextMap = new Map<
      string,
      {
        label: string;
        text: string;
        prose?: boolean;
        /** The answer on its own, as HTML. Set only when `outputHtml`. */
        valueHtml?: string;
      }
    >();
    sortedQuestions.forEach((question) => {
      if (excludedQuestionIds?.has(question.id)) return;

      // Display-only types render from the question config, not an answer, so
      // they print even though no NoteAnswer exists for them.
      if (question.questionType === QuestionType.DISCLAIMER) {
        const content = (
          question.data as Disclaimer | undefined
        )?.content?.trim();
        if (content) {
          answerTextMap.set(question.id, {
            label: "",
            text: content,
            // Through the block builder, not one `<p>`: a clinic's disclaimer is
            // typed into a textarea, and HTML collapses the line breaks it was
            // written with.
            valueHtml: plainTextToPrintHtml(content),
          });
        }
        return;
      }
      if (question.questionType === QuestionType.STATEMENT) {
        const points = (question.data as Statement | undefined)?.points ?? [];
        if (points.length > 0) {
          const label = question.data?.name?.trim();
          const bullets = points
            .map((p) => `${options.plainText ? "•" : "-"} ${p}`)
            .join("\n");
          answerTextMap.set(question.id, {
            label: label ?? "",
            text: label ? `${label}\n${bullets}` : bullets,
            valueHtml: `<ul>${points
              .map((p) => `<li>${escapeNoteHtml(p)}</li>`)
              .join("")}</ul>`,
          });
        }
        return;
      }

      const answer = answerByQuestionId.get(question.id);
      if (!answer) return;

      const answerData = answer.data;
      const questionLabel =
        question.data.name || `Question ${question.position}`;

      // Render a drawn signature as a markdown image rather than dumping the
      // raw data-URL into the text.
      if (question.questionType === QuestionType.SIGNATURE) {
        if (typeof answerData.value === "string" && answerData.value) {
          answerTextMap.set(question.id, {
            label: questionLabel,
            text: options.plainText
              ? `${questionLabel}: [Signature captured]`
              : `${questionLabel}:\n\n![${questionLabel}](${answerData.value})`,
            valueHtml: signatureImageHtml(answerData.value, questionLabel),
          });
        }
        return;
      }

      // Provider signature: render the selected provider's stamped signature
      // image plus their name.
      if (question.questionType === QuestionType.PROVIDER_SIGNATURE) {
        const signatureUrl =
          typeof answerData.signatureImageUrl === "string"
            ? answerData.signatureImageUrl
            : null;
        const providerName =
          typeof answerData.providerName === "string"
            ? answerData.providerName
            : "";
        if (signatureUrl) {
          answerTextMap.set(question.id, {
            label: questionLabel,
            text: options.plainText
              ? `${questionLabel}: ${providerName || "[Signature captured]"}`
              : `${questionLabel}:\n\n![${providerName || questionLabel}](${signatureUrl})${
                  providerName ? `\n\n${providerName}` : ""
                }`,
            valueHtml:
              signatureImageHtml(signatureUrl, providerName || questionLabel) +
              (providerName ? `<p>${escapeNoteHtml(providerName)}</p>` : ""),
          });
        } else if (providerName) {
          answerTextMap.set(question.id, {
            label: questionLabel,
            text: `${questionLabel}: ${providerName} (no signature on file)`,
            valueHtml: `<p>${escapeNoteHtml(providerName)} (no signature on file)</p>`,
          });
        }
        return;
      }

      let answerValue: string;
      // The answer as HTML. Rich answers keep their own markup; everything else
      // is escaped text, so nothing unescaped reaches the printed document.
      let answerValueHtml: string;
      // Rich answers become multi-line Markdown (headings/lists/quotes), which
      // must start on its own line — not glued after "Label: " — to render.
      let isRichAnswer = false;
      if (
        question.questionType === QuestionType.FEET_INCHES_HEIGHT &&
        answerData.value &&
        typeof answerData.value === "object"
      ) {
        const { feet, inches } = answerData.value;
        answerValue = `${feet}'${inches}"`;
        answerValueHtml = escapeNoteHtml(answerValue);
      } else if (isIcd10CodesQuestion(question)) {
        const lines = icd10CodeLines(answerData, options.icd10Descriptions);
        if (lines.length === 0) return;
        answerTextMap.set(question.id, {
          label: questionLabel,
          text: `${questionLabel}:\n${icd10LinesToText(lines, !!options.plainText)}`,
          prose: true,
          valueHtml: icd10LinesToHtml(lines),
        });
        return;
      } else if (answerData.values && Array.isArray(answerData.values)) {
        answerValue = answerData.values
          .map((v) => stripHtmlToText(String(v)))
          .join(", ");
        answerValueHtml = escapeNoteHtml(answerValue);
      } else if (answerData.value !== null && answerData.value !== undefined) {
        const raw = String(answerData.value);
        isRichAnswer = looksLikeRichHtml(raw);
        // Flattened for every text caller. Print takes the HTML path below and
        // keeps the answer's own markup; the remaining text callers all copy to
        // the clipboard, where markup would paste as literal syntax.
        answerValue = stripHtmlToText(raw);
        // The HTML path keeps the answer's own markup instead of flattening it
        // through Markdown, so underline and colour survive.
        // Only a narrative answer gets block markup; a discrete one is rendered
        // inside a paragraph, where a list cannot legally nest.
        answerValueHtml = isRichAnswer
          ? richHtmlToSafeHtml(raw)
          : plainTextToPrintHtml(
              stripHtmlToText(raw),
              question.questionType === QuestionType.TEXT_AREA,
            );
      } else {
        return; // Skip empty answers
      }

      answerTextMap.set(question.id, {
        label: questionLabel,
        text: isRichAnswer
          ? `${questionLabel}:\n\n${answerValue}`
          : `${questionLabel}: ${answerValue}`,
        prose: isRichAnswer || question.questionType === QuestionType.TEXT_AREA,
        valueHtml: answerValueHtml,
      });
    });

    // Emit answers in question order, grouping sections together with a header
    const outputParts: string[] = [];
    const processedSectionIds = new Set<string>();
    const processedQuestionIds = new Set<string>();

    for (const question of sortedQuestions) {
      if (processedQuestionIds.has(question.id)) continue;

      const sectionId = question.sectionId;
      if (sectionId && !processedSectionIds.has(sectionId)) {
        processedSectionIds.add(sectionId);
        const sectionName = sectionNameMap.get(sectionId);
        const sectionAnswers: string[] = [];

        for (const sectionQ of sortedQuestions) {
          if (sectionQ.sectionId !== sectionId) continue;
          processedQuestionIds.add(sectionQ.id);
          const answerText = answerTextMap.get(sectionQ.id);
          if (answerText) {
            const isDuplicateSectionLabel =
              options.dedupeSectionLabels &&
              normalizeNoteLabel(sectionName) ===
                normalizeNoteLabel(answerText.label);
            const rendered = options.outputHtml
              ? htmlLabelledBlock(
                  answerText.valueHtml ?? "",
                  isDuplicateSectionLabel ? "" : answerText.label,
                  !!answerText.prose,
                )
              : isDuplicateSectionLabel
                ? stripDuplicateLeadingLabel(answerText.text, answerText.label)
                : options.proseLayout && answerText.prose
                  ? proseLabelledBlock(
                      answerText.text,
                      answerText.label,
                      !!options.plainText,
                    )
                  : options.plainText
                    ? answerText.text
                    : boldLeadingLabel(answerText.text, answerText.label);
            // Only prose layout yields "" (blank answer); every other branch
            // returns the text unchanged, so this preserves existing output.
            if (rendered.trim()) sectionAnswers.push(rendered);
          }
        }

        if (sectionAnswers.length > 0) {
          if (sectionName) {
            // In prose layout the question labels are already h3 subheadings,
            // so the section becomes the h2 division above them.
            outputParts.push(
              options.outputHtml
                ? `<h2>${escapeNoteHtml(sectionName)}</h2>`
                : options.plainSectionHeadings
                  ? sectionName
                  : options.proseLayout
                    ? `## ${sectionName}`
                    : `### ${sectionName}`,
            );
          }
          outputParts.push(...sectionAnswers);
        }
      } else if (!sectionId) {
        processedQuestionIds.add(question.id);
        const answerText = answerTextMap.get(question.id);
        if (answerText) {
          const rendered = options.outputHtml
            ? htmlLabelledBlock(
                answerText.valueHtml ?? "",
                answerText.label,
                !!answerText.prose,
              )
            : options.proseLayout && answerText.prose
              ? proseLabelledBlock(
                  answerText.text,
                  answerText.label,
                  !!options.plainText,
                )
              : options.plainText
                ? answerText.text
                : boldLeadingLabel(answerText.text, answerText.label);
          if (rendered.trim()) outputParts.push(rendered);
        }
      }
    }

    // HTML blocks carry their own spacing; Markdown needs the blank line.
    if (outputParts.length > 0) {
      return outputParts.join(options.outputHtml ? "\n" : "\n\n");
    }

    // Empty output has two causes that must not look alike to callers, because
    // they read `undefined` as "no structured content" and print the raw
    // `summary` instead. When a condition or an exclusion is what emptied the
    // output, that fallback would hand the provider the very content they
    // withheld, so return an empty string: filtered to nothing, not absent.
    return hasWithheldPrintableContent(
      noteDetail,
      excludedQuestionIds,
      localAnswerChanges,
    )
      ? ""
      : undefined;
  }
  if (!noteDetail.summary) return undefined;
  return options.outputHtml
    ? plainTextToPrintHtml(stripHtmlToText(noteDetail.summary))
    : stripHtmlToText(noteDetail.summary);
}

const escapeHtmlText = (value: string): string =>
  value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/**
 * HTML rendering of a note's answers for the clipboard, so pasting into a
 * rich-text editor (email template, treatment plan) keeps the note's structure —
 * section headings, per-question labels, paragraphs and lists — instead of
 * collapsing into one run-on paragraph the way a plain-text copy does.
 *
 * Rich-text answers are already block HTML from the note editor and carry
 * through verbatim; plain answers are escaped into `<p>`. Question ordering,
 * section grouping and label de-duplication mirror {@link formatNoteAnswersToText}
 * so the copied HTML matches the note on screen. Returns undefined when there is
 * no structured content (callers fall back to the plain-text copy).
 */
export function formatNoteAnswersToHtml(
  noteDetail: NoteDetail,
  excludedQuestionIds?: ReadonlySet<string>,
  options: { dedupeSectionLabels?: boolean } = {},
  localAnswerChanges?: NoteAnswerChanges,
): string | undefined {
  if (!noteDetail.template) {
    return noteDetail.summary || undefined;
  }
  const mergedAnswers = mergeAnswersWithLocalChanges(
    noteDetail,
    localAnswerChanges,
  );
  const answerByQuestionId = new Map(
    mergedAnswers.map((a) => [a.questionId, a]),
  );
  const sortedQuestions = visibleSortedQuestions(
    noteDetail,
    localAnswerChanges,
  );

  const sectionNameMap = new Map<string, string>();
  for (const section of noteDetail.template.sections ?? []) {
    sectionNameMap.set(section.id, section.name);
  }

  // Per question, a renderer that emits its block HTML with or without its
  // leading label (dropped when it would just repeat the section heading).
  const renderers = new Map<
    string,
    { label: string; render: (includeLabel: boolean) => string }
  >();

  const labelPrefix = (label: string, includeLabel: boolean) =>
    includeLabel && label ? `<strong>${escapeHtmlText(label)}</strong>: ` : "";
  const paragraphs = (text: string) =>
    text
      .split("\n")
      .filter((line) => line.trim())
      .map((line) => `<p>${escapeHtmlText(line)}</p>`)
      .join("");

  for (const question of sortedQuestions) {
    if (excludedQuestionIds?.has(question.id)) continue;

    if (question.questionType === QuestionType.DISCLAIMER) {
      const content = (
        question.data as Disclaimer | undefined
      )?.content?.trim();
      if (content) {
        renderers.set(question.id, {
          label: "",
          render: () => paragraphs(content),
        });
      }
      continue;
    }
    if (question.questionType === QuestionType.STATEMENT) {
      const points = (question.data as Statement | undefined)?.points ?? [];
      if (points.length > 0) {
        const label = question.data?.name?.trim() ?? "";
        renderers.set(question.id, {
          label,
          render: (includeLabel) =>
            `${includeLabel && label ? `<p><strong>${escapeHtmlText(label)}</strong></p>` : ""}<ul>${points
              .map((p) => `<li>${escapeHtmlText(p)}</li>`)
              .join("")}</ul>`,
        });
      }
      continue;
    }

    const answer = answerByQuestionId.get(question.id);
    if (!answer) continue;
    const answerData = answer.data;
    const label = question.data.name || `Question ${question.position}`;

    if (question.questionType === QuestionType.SIGNATURE) {
      if (typeof answerData.value === "string" && answerData.value) {
        renderers.set(question.id, {
          label,
          render: (includeLabel) =>
            `<p>${labelPrefix(label, includeLabel)}[Signature captured]</p>`,
        });
      }
      continue;
    }
    if (question.questionType === QuestionType.PROVIDER_SIGNATURE) {
      const providerName =
        typeof answerData.providerName === "string"
          ? answerData.providerName
          : "";
      const hasSignature =
        typeof answerData.signatureImageUrl === "string" &&
        answerData.signatureImageUrl.length > 0;
      if (providerName || hasSignature) {
        renderers.set(question.id, {
          label,
          render: (includeLabel) =>
            `<p>${labelPrefix(label, includeLabel)}${escapeHtmlText(
              providerName || "[Signature captured]",
            )}</p>`,
        });
      }
      continue;
    }

    let inline: string | null = null;
    let richHtml: string | null = null;
    if (
      question.questionType === QuestionType.FEET_INCHES_HEIGHT &&
      answerData.value &&
      typeof answerData.value === "object"
    ) {
      const { feet, inches } = answerData.value;
      inline = `${feet}'${inches}"`;
    } else if (isIcd10CodesQuestion(question)) {
      const lines = icd10CodeLines(answerData);
      if (lines.length === 0) continue;
      richHtml = icd10LinesToHtml(lines);
    } else if (answerData.values && Array.isArray(answerData.values)) {
      inline = answerData.values
        .map((v) => stripHtmlToText(String(v)))
        .join(", ");
    } else if (answerData.value !== null && answerData.value !== undefined) {
      const raw = String(answerData.value);
      // Sanitized and re-nested rather than shipped verbatim: this HTML goes
      // onto the clipboard, and a sub-list the model wrote beside its parent
      // item is what made pasted notes break into blank-separated blocks in the
      // first place (#9821).
      if (looksLikeRichHtml(raw)) richHtml = richHtmlToSafeHtml(raw);
      else inline = stripHtmlToText(raw);
    } else {
      continue;
    }

    renderers.set(question.id, {
      label,
      render: (includeLabel) =>
        richHtml !== null
          ? `${includeLabel && label ? `<p><strong>${escapeHtmlText(label)}</strong></p>` : ""}${richHtml}`
          : `<p>${labelPrefix(label, includeLabel)}${escapeHtmlText(inline ?? "")}</p>`,
    });
  }

  const outputParts: string[] = [];
  const processedSectionIds = new Set<string>();
  const processedQuestionIds = new Set<string>();

  for (const question of sortedQuestions) {
    if (processedQuestionIds.has(question.id)) continue;
    const sectionId = question.sectionId;

    if (sectionId && !processedSectionIds.has(sectionId)) {
      processedSectionIds.add(sectionId);
      const sectionName = sectionNameMap.get(sectionId);
      const sectionBlocks: string[] = [];

      for (const sectionQ of sortedQuestions) {
        if (sectionQ.sectionId !== sectionId) continue;
        processedQuestionIds.add(sectionQ.id);
        const renderer = renderers.get(sectionQ.id);
        if (!renderer) continue;
        const isDuplicateSectionLabel =
          options.dedupeSectionLabels &&
          normalizeNoteLabel(sectionName) ===
            normalizeNoteLabel(renderer.label);
        sectionBlocks.push(renderer.render(!isDuplicateSectionLabel));
      }

      if (sectionBlocks.length > 0) {
        if (sectionName)
          outputParts.push(`<h3>${escapeHtmlText(sectionName)}</h3>`);
        outputParts.push(...sectionBlocks);
      }
    } else if (!sectionId) {
      processedQuestionIds.add(question.id);
      const renderer = renderers.get(question.id);
      if (renderer) outputParts.push(renderer.render(true));
    }
  }

  if (outputParts.length > 0) return outputParts.join("");
  return noteDetail.summary || undefined;
}

export interface PrintableNotePart {
  questionId: string;
  label: string;
}

export interface PrintableNoteGroup {
  sectionId: string | null;
  sectionName: string | null;
  parts: PrintableNotePart[];
}

/**
 * Whether a question's answer produces printable text. Mirrors the emit/skip
 * branches in {@link formatNoteAnswersToText} so the print-selection dialog
 * lists exactly the parts that would otherwise appear in the printout.
 */
function answerProducesText(
  question: NoteQuestionDetail,
  answer: NoteAnswerDetail,
): boolean {
  const answerData = answer.data;
  if (
    question.questionType === QuestionType.FEET_INCHES_HEIGHT &&
    answerData.value &&
    typeof answerData.value === "object"
  ) {
    return true;
  }
  // Signature only prints when a non-empty data URL was captured — match the
  // emit guard in formatNoteAnswersToText so the dialog doesn't list a blank one.
  if (question.questionType === QuestionType.SIGNATURE) {
    return typeof answerData.value === "string" && answerData.value.length > 0;
  }
  // Provider signature prints when a provider was selected (with or without an
  // adopted signature image — the "no signature on file" note still prints).
  // Mirror the emit condition in formatNoteAnswersToText (signatureImageUrl OR
  // providerName) so the print-selection dialog lists exactly what will print.
  if (question.questionType === QuestionType.PROVIDER_SIGNATURE) {
    return (
      (typeof answerData.signatureImageUrl === "string" &&
        answerData.signatureImageUrl.length > 0) ||
      (typeof answerData.providerName === "string" &&
        answerData.providerName.length > 0)
    );
  }
  if (answerData.values && Array.isArray(answerData.values)) return true;
  return answerData.value !== null && answerData.value !== undefined;
}

/**
 * Build the ordered list of a note's printable parts, grouped by section in the
 * same order {@link formatNoteAnswersToText} emits them. Used to populate the
 * print-time "select what to include" dialog.
 *
 * `localAnswerChanges` behaves the same as in {@link formatNoteAnswersToText}:
 * optional, and only changes output when a caller with draft state passes it.
 */
export function getPrintableNoteParts(
  noteDetail: NoteDetail,
  localAnswerChanges?: NoteAnswerChanges,
): PrintableNoteGroup[] {
  if (!noteDetail.template) return [];

  const answerByQuestionId = new Map(
    mergeAnswersWithLocalChanges(noteDetail, localAnswerChanges).map((a) => [
      a.questionId,
      a,
    ]),
  );

  // Conditions gate the print-selection dialog too, so it lists exactly the parts
  // that would appear in the printout.
  const sortedQuestions = visibleSortedQuestions(
    noteDetail,
    localAnswerChanges,
  );
  const sectionNameMap = new Map<string, string>();
  for (const section of noteDetail.template.sections ?? []) {
    sectionNameMap.set(section.id, section.name);
  }

  const partFor = (question: NoteQuestionDetail): PrintableNotePart | null => {
    const label = question.data.name || `Question ${question.position}`;

    // Display-only types are printable from their config (no answer required).
    if (question.questionType === QuestionType.DISCLAIMER) {
      return (question.data as Disclaimer | undefined)?.content?.trim()
        ? {
            questionId: question.id,
            label: question.data?.name || "Disclaimer",
          }
        : null;
    }
    if (question.questionType === QuestionType.STATEMENT) {
      return ((question.data as Statement | undefined)?.points?.length ?? 0) > 0
        ? { questionId: question.id, label: question.data?.name || "Statement" }
        : null;
    }

    const answer = answerByQuestionId.get(question.id);
    if (!answer || !answerProducesText(question, answer)) return null;
    return { questionId: question.id, label };
  };

  const groups: PrintableNoteGroup[] = [];
  const processedSectionIds = new Set<string>();
  const processedQuestionIds = new Set<string>();

  for (const question of sortedQuestions) {
    if (processedQuestionIds.has(question.id)) continue;

    const sectionId = question.sectionId;
    if (sectionId && !processedSectionIds.has(sectionId)) {
      processedSectionIds.add(sectionId);
      const parts: PrintableNotePart[] = [];
      for (const sectionQ of sortedQuestions) {
        if (sectionQ.sectionId !== sectionId) continue;
        processedQuestionIds.add(sectionQ.id);
        const part = partFor(sectionQ);
        if (part) parts.push(part);
      }
      if (parts.length > 0) {
        groups.push({
          sectionId,
          sectionName: sectionNameMap.get(sectionId) ?? null,
          parts,
        });
      }
    } else if (!sectionId) {
      processedQuestionIds.add(question.id);
      const part = partFor(question);
      if (part)
        groups.push({ sectionId: null, sectionName: null, parts: [part] });
    }
  }

  return groups;
}

export interface SignOffSigner {
  name: string;
  credentials?: string | null;
  signatureImageUrl?: string | null;
  signedDate?: string | null;
}

/**
 * Append a "Signatures" block to printed note content for each provider who
 * signed off. Renders the signer's adopted signature image when available, and
 * falls back to their typed name plus "(no signature on file)" otherwise.
 * Returns the content unchanged when there are no signers.
 */
export function appendSignOffSignatures(
  content: string,
  signers: SignOffSigner[],
  options: { plainText?: boolean; outputHtml?: boolean } = {},
): string {
  const valid = signers.filter((s) => s.name);
  if (valid.length === 0) return content;

  const blocks = valid.map((s) => {
    // The attestation phrase, the credentials and a time recorded to the minute
    // are all required for an electronic signature to stand as authentication of
    // the entry — a bare name and date does not.
    const signedBy = s.credentials ? `${s.name}, ${s.credentials}` : s.name;
    const nameLine = `Electronically signed by: ${signedBy}`;
    // Through the shared helpers, which resolve the clinic/user timezone. The
    // browser's own zone can put the signature on a different hour — or a
    // different calendar date — than every other timestamp on the document.
    const dateLine = s.signedDate
      ? `Date: ${formatDate(s.signedDate)}    Time: ${formatTime(s.signedDate)}`
      : "";
    if (options.outputHtml) {
      const image = s.signatureImageUrl
        ? signatureImageHtml(s.signatureImageUrl, s.name)
        : "";
      const suffix = s.signatureImageUrl ? "" : " (no signature on file)";
      return [
        image,
        `<p>${escapeNoteHtml(`${nameLine}${suffix}`)}</p>`,
        dateLine ? `<p>${escapeNoteHtml(dateLine)}</p>` : "",
      ]
        .filter(Boolean)
        .join("");
    }
    if (options.plainText || !s.signatureImageUrl) {
      const suffix = s.signatureImageUrl ? "" : " (no signature on file)";
      return [`${nameLine}${suffix}`, dateLine].filter(Boolean).join("\n");
    }
    return [`![${s.name}](${s.signatureImageUrl})`, nameLine, dateLine]
      .filter(Boolean)
      .join("\n\n");
  });

  if (options.outputHtml) {
    return [content, `<h3>Signatures</h3>`, ...blocks]
      .filter(Boolean)
      .join("\n");
  }
  const heading = options.plainText ? "Signatures" : "### Signatures";
  return [content, heading, ...blocks].filter(Boolean).join("\n\n");
}

// ============================================================================
// Template Migration Helper utilities
// ============================================================================

/**
 * Checks if a question is a legacy migrated TEXT_AREA question.
 * Legacy questions were created by the scribe_as_a_form migration and contain
 * the old content_template in their description field.
 *
 * @param question - The question to check
 * @returns true if the question is a legacy migrated question
 */
export function isLegacyMigratedQuestion(
  question: NoteQuestionDetail,
): boolean {
  if (question.questionType !== QuestionType.TEXT_AREA) {
    return false;
  }

  const description = question.data?.description;
  if (!description || typeof description !== "string") {
    return false;
  }

  return description.startsWith(LEGACY_TEMPLATE_DESCRIPTION_PREFIX);
}

/**
 * Extracts the original content_template from a legacy migrated question's description.
 *
 * @param question - The legacy question to extract from
 * @returns The original content_template, or empty string if not found
 */
export function extractContentTemplateFromLegacyQuestion(
  question: NoteQuestionDetail,
): string {
  const description = question.data?.description;
  if (!description || typeof description !== "string") {
    return "";
  }

  if (!description.startsWith(LEGACY_TEMPLATE_DESCRIPTION_PREFIX)) {
    return "";
  }

  return description.slice(LEGACY_TEMPLATE_DESCRIPTION_PREFIX.length);
}

/**
 * Finds the legacy migrated question in a template, if one exists.
 *
 * @param template - The template to search
 * @returns The legacy question if found, undefined otherwise
 */
export function findLegacyMigratedQuestion(
  template: NoteTemplateTableItem | undefined,
): NoteQuestionDetail | undefined {
  if (!template?.questions) {
    return undefined;
  }

  return template.questions.find(
    (q) => !q.isArchived && isLegacyMigratedQuestion(q),
  );
}

/**
 * Get a display title for a note, falling back to a generated default
 * when no title is set.
 *
 * @param note - Object with optional title, patient, and templateId
 * @param templateName - Template name to use in fallback (optional)
 * @returns The note title or a fallback string
 */
export function getNoteTitleFallback(
  note: {
    title?: string;
    patient?: { firstName?: string; lastName?: string };
  },
  templateName?: string,
): string {
  if (note.title) return note.title;

  const firstName = note.patient?.firstName || "";
  const lastName = note.patient?.lastName || "";
  const patientName = `${firstName} ${lastName}`.trim() || "Unknown";
  return `${patientName} - ${templateName || "Note"}`;
}

/**
 * Whether a note has answers from generation or a provider filling it in.
 *
 * Adding a template image during a visit stores an empty SCRIBE_ATTACHMENTS
 * answer before the note is generated, so "has any answer" is not the same as
 * "generated". If neither template carries the attachments question, every
 * answer counts, so an unloaded template never reads a finished note as a draft.
 */
export function hasGeneratedAnswers(
  note: NoteDetail | null | undefined,
  template?: NoteTemplateTableItem | null,
): boolean {
  if (!note?.answers?.length) return false;
  const attachmentsQuestionId = (
    findScribeAttachmentsQuestion(note.template) ??
    findScribeAttachmentsQuestion(template)
  )?.id;
  return note.answers.some((a) => a.questionId !== attachmentsQuestionId);
}
